// Pure path <-> view mapping for the SPA. No store imports — the store and
// useRouteSync both build on this, so it must stay side-effect free.

export type AppView =
  | { name: "home" }
  | { name: "welcome" }
  | { name: "console" }
  | { name: "projects" }
  | { name: "project"; id: string }
  | { name: "assistants" }
  | { name: "assistant"; id: string }
  | { name: "servers" }
  | { name: "server"; id: string }
  | { name: "machines" };

export interface RouteSession {
  machineId?: string;
  slug: string;
  sessionId: string;
}

export interface ParsedRoute {
  view: AppView;
  session?: RouteSession;
}

interface SelectedLike {
  source: string | null;
  slug: string | null;
  sessionId: string | null;
}

// Console (session) regexes copied verbatim from the original useUrlSync so
// existing deep links keep resolving byte-for-byte identically.
const REMOTE_SESSION_RE = /^\/m\/([^/]+)\/s\/([^/]+)\/([^/]+)\/?$/;
const LOCAL_SESSION_RE = /^\/s\/([^/]+)\/([^/]+)\/?$/;

function parseListOrDetail(
  pathname: string,
  prefix: string,
  list: AppView,
  detail: (id: string) => AppView,
): AppView | null {
  if (pathname === prefix || pathname === `${prefix}/`) return list;
  const m = pathname.match(new RegExp(`^${prefix}/([^/]+)/?$`));
  if (!m) return null;
  try {
    return detail(decodeURIComponent(m[1]));
  } catch {
    return null;
  }
}

export function parsePath(pathname: string): ParsedRoute {
  const remote = pathname.match(REMOTE_SESSION_RE);
  if (remote) {
    try {
      return {
        view: { name: "console" },
        session: {
          machineId: decodeURIComponent(remote[1]),
          slug: decodeURIComponent(remote[2]),
          sessionId: decodeURIComponent(remote[3]),
        },
      };
    } catch {
      return { view: { name: "home" } };
    }
  }

  const local = pathname.match(LOCAL_SESSION_RE);
  if (local) {
    try {
      return {
        view: { name: "console" },
        session: {
          slug: decodeURIComponent(local[1]),
          sessionId: decodeURIComponent(local[2]),
        },
      };
    } catch {
      return { view: { name: "home" } };
    }
  }

  const projects = parseListOrDetail(pathname, "/projects", { name: "projects" }, (id) => ({ name: "project", id }));
  if (projects) return { view: projects };

  const assistants = parseListOrDetail(pathname, "/assistants", { name: "assistants" }, (id) => ({ name: "assistant", id }));
  if (assistants) return { view: assistants };

  const servers = parseListOrDetail(pathname, "/servers", { name: "servers" }, (id) => ({ name: "server", id }));
  if (servers) return { view: servers };

  if (pathname === "/machines" || pathname === "/machines/") {
    return { view: { name: "machines" } };
  }

  if (pathname === "/welcome" || pathname === "/welcome/") {
    return { view: { name: "welcome" } };
  }

  // "/" and anything unrecognized both land on home.
  return { view: { name: "home" } };
}

export function buildPath(view: AppView, selected?: SelectedLike): string {
  switch (view.name) {
    case "home":
      return "/";
    case "welcome":
      return "/welcome";
    case "console": {
      const source = selected?.source ?? null;
      const slug = selected?.slug ?? null;
      const sessionId = selected?.sessionId ?? null;
      if (!source || !slug || !sessionId) return "/";
      if (source !== "local") {
        return `/m/${encodeURIComponent(source)}/s/${encodeURIComponent(slug)}/${encodeURIComponent(sessionId)}`;
      }
      return `/s/${encodeURIComponent(slug)}/${encodeURIComponent(sessionId)}`;
    }
    case "projects":
      return "/projects";
    case "project":
      return `/projects/${encodeURIComponent(view.id)}`;
    case "assistants":
      return "/assistants";
    case "assistant":
      return `/assistants/${encodeURIComponent(view.id)}`;
    case "servers":
      return "/servers";
    case "server":
      return `/servers/${encodeURIComponent(view.id)}`;
    case "machines":
      return "/machines";
  }
}
